import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import { AuditReport, AuditStatus, Finding, Severity } from './contentAudit.types';
import FindingList from './FindingList';
import RubricDialog from './RubricDialog';
import SPAAuditSummary from './SPAAuditSummary';
import SPADisclosure from './SPADisclosure';
import SPASectionGroup from './SPASectionGroup';
import SPAButton from '../../shared/SPAButton';
import SPAPageHeader from '../../shared/SPAPageHeader';

const POLL_INTERVAL = 2000;

const SEVERITY_RANK: Record<Severity, number> = { High: 0, Low: 2, Med: 1 };

const SOURCE_SLUGS: Record<string, string> = {
  'Enhancing Learning': 'Teacher resources',
  'Interactive Student Notebook': 'Notes',
  'Library Questions': 'Questions',
  'Library Text': 'Text',
  'Student Edition': 'Reading',
};

const sourceSlug = (source: string) => SOURCE_SLUGS[source] ?? source;

const runButtonLabel = (isRunning: boolean, hasReport: boolean): string => {
  if (isRunning) return 'Auditing…';
  return hasReport ? 'Re-run audit' : 'Run audit';
};

interface Group {
  findings: Finding[];
  rank: number;
  slug: string;
}

// Group findings by section + material (Reading/Notes), worst-severity groups first.
const groupBySection = (findings: Finding[]): Group[] => {
  const map = new Map<string, Finding[]>();
  findings.forEach((finding) => {
    const key = `${finding.section_title} · ${sourceSlug(finding.source)}`;
    const arr = map.get(key) ?? [];
    arr.push(finding);
    map.set(key, arr);
  });

  return Array.from(map.entries())
    .map(([slug, items]) => ({
      findings: [...items].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]),
      rank: Math.min(...items.map(item => SEVERITY_RANK[item.severity])),
      slug,
    }))
    .sort((a, b) => a.rank - b.rank);
};

const parseReport = (raw: unknown): AuditReport | null => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw) as AuditReport;
  } catch {
    return null;
  }
};

const Spinner = () => (
  <div
    aria-hidden
    className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-spa-border"
    style={{ borderTopColor: 'var(--spa-accent)' }}
  />
);

// SPA fork of admin/ContentAudit/ContentAudit — hosts either a lesson audit (lessonId) or a
// library material audit (referenceMaterialId); exactly one of the two ids should be passed.
interface Props {
  auditable?: boolean;
  auditing?: boolean;
  lessonId?: number;
  programId?: number;
  referenceMaterialId?: number;
  rubric?: string;
  title: string;
  onAuditingChange?: (auditing: boolean) => void;
  onReportChange?: () => void;
}

const AuditReportPane = ({
  auditable = true, auditing = false, lessonId, programId, referenceMaterialId, rubric = 'fl_pr', title,
  onAuditingChange, onReportChange,
}: Props) => {
  const [status, setStatus] = useState<AuditStatus>('idle');
  const [report, setReport] = useState<AuditReport | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [freshness, setFreshness] = useState<string | null>(null);
  const [sections, setSections] = useState<Array<{ id: number; title: string }>>([]);
  const [loading, setLoading] = useState(true);

  const auditPath = referenceMaterialId
    ? Routes.plato_api_content_audit_reference_material_path(referenceMaterialId)
    : Routes.plato_api_content_audit_lesson_path(lessonId as number);
  const exportHref = referenceMaterialId
    ? Routes.plato_api_content_audit_export_reference_material_path(referenceMaterialId, { rubric })
    : Routes.plato_api_content_audit_export_lesson_path(lessonId as number, { rubric });
  const subjectLabel = referenceMaterialId
    ? 'Library text and questions'
    : 'Student text, notebook, and Enhancing Learning';

  const pollTimerRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const fetchLatestReport = useCallback(async () => {
    const { data } = await axios.get(auditPath, { params: { rubric } });
    setSections(data.sections ?? []);
    const parsed = parseReport(data.report);
    if (parsed) {
      setReport(parsed);
      setGeneratedAt(data.generated_at);
      setFreshness(data.freshness ?? null);
      onReportChange?.();
    } else if (data.report) {
      setError('Could not read the stored review. Re-run the audit or refresh the page.');
    }
    return parsed;
  }, [auditPath, onReportChange, rubric]);

  useEffect(() => {
    fetchLatestReport()
      .catch(() => setError('Could not load the latest review. Refresh to try again.'))
      .finally(() => setLoading(false));
    return () => stopPolling();
  }, [fetchLatestReport, stopPolling]);

  const pollJobStatus = useCallback((jobId: string) => {
    pollTimerRef.current = window.setInterval(async () => {
      try {
        const { data } = await axios.get(Routes.plato_api_job_status_path(jobId));
        const jobStatus = data.data?.status;

        if (jobStatus === 'rate_limited') {
          stopPolling();
          setStatus('rate_limited');
          onAuditingChange?.(false);
        } else if (jobStatus === 'failed') {
          stopPolling();
          setStatus('failed');
          setError(data.data?.error?.message || 'The audit failed. Please try again.');
          onAuditingChange?.(false);
        } else if (jobStatus === 'completed') {
          stopPolling();
          await fetchLatestReport();
          setStatus('completed');
          onAuditingChange?.(false);
        }
      } catch {
        stopPolling();
        setStatus('failed');
        setError('The audit failed. Please try again.');
        onAuditingChange?.(false);
      }
    }, POLL_INTERVAL);
  }, [fetchLatestReport, onAuditingChange, stopPolling]);

  const runAudit = useCallback(async () => {
    setStatus('running');
    setError(null);
    onAuditingChange?.(true);

    try {
      const { data } = await axios.post(auditPath, { rubric });

      if (data.status === 'rate_limited') {
        setStatus('rate_limited');
        onAuditingChange?.(false);
        return;
      }
      pollJobStatus(data.job_id);
    } catch {
      setStatus('failed');
      setError('Could not start the audit. Please try again.');
      onAuditingChange?.(false);
    }
  }, [auditPath, onAuditingChange, pollJobStatus, rubric]);

  // The section editor 404s without its program/lesson context params (Admin::SectionsController
  // find_models does Program.find(params[:program_id])).
  const sectionEditHref = (sectionTitle: string) => {
    const match = sections.find(section => section.title === sectionTitle);
    if (!match) return undefined;
    return Routes.edit_admin_section_path(match.id, { lesson_id: lessonId, program_id: programId });
  };

  // A rail-triggered run (auditing prop) reads the same as one started here.
  const isRunning = status === 'running' || auditing;
  const risks = report?.findings.filter(finding => !finding.is_asset) ?? [];
  const assets = report?.findings.filter(finding => finding.is_asset) ?? [];
  // Grouped by ACTION (content team request): Must edits foregrounded, Edit candidates in their own
  // section-grouped disclosure, and everything advisory (Leave & defend / Note only + strengths)
  // folded into the bottom group.
  const mustEditGroups = groupBySection(risks.filter(finding => finding.action === 'Must edit'));
  const editCandidates = risks.filter(finding => finding.action === 'Edit candidate');
  const editCandidateGroups = groupBySection(editCandidates);
  const keepers = [
    ...assets,
    ...risks.filter(finding => finding.action !== 'Must edit' && finding.action !== 'Edit candidate'),
  ];

  return (
    <section>
      <SPAPageHeader
        actions={(
          <div className="tw-flex tw-items-center tw-gap-4">
            {referenceMaterialId && (
              <a
                className="tw-text-sm tw-font-bold tw-text-spa-accent-text tw-no-underline hover:tw-underline"
                href={Routes.edit_admin_reference_material_path(referenceMaterialId)}
                rel="noreferrer"
                target="_blank"
              >
                Edit in admin ↗
              </a>
            )}
            <RubricDialog
              contentType={referenceMaterialId ? 'reference_material' : 'lesson'}
              rubric={rubric}
            />
            {report && !isRunning && (
              <a
                className="tw-text-sm tw-font-bold tw-text-spa-accent-text tw-no-underline hover:tw-underline"
                href={exportHref}
              >
                Export CSV
              </a>
            )}
            {auditable && (
              <SPAButton disabled={isRunning} variant="primary" onClick={runAudit}>
                {runButtonLabel(isRunning, Boolean(report))}
              </SPAButton>
            )}
          </div>
        )}
        title={title}
      />
      <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-secondary">{subjectLabel}</p>

      <div className="tw-flex tw-flex-col tw-gap-6">

        {isRunning && (
        <div className="tw-flex tw-items-center tw-gap-2">
          <Spinner />
          <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-secondary">
            Reviewing this {referenceMaterialId ? 'material' : 'lesson'}… this can take a moment.
          </p>
        </div>
        )}
        {status === 'rate_limited' && (
        <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-secondary">
          Rate limited by the AI gateway. Please try again later.
        </p>
        )}
        {error && <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-secondary">{error}</p>}

        {report && !isRunning && (
        <>
          <SPAAuditSummary generatedAt={generatedAt} report={report} />

          {freshness === 'outdated' && (
            <div className="tw-flex tw-items-center tw-gap-1.5">
              <span
                aria-hidden
                className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-spa-warning"
              />
              <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-secondary">
                Content has changed since this review — re-run to refresh.
              </p>
            </div>
          )}

          {mustEditGroups.length > 0 && (
            <div className="tw-flex tw-flex-col tw-gap-5">
              {mustEditGroups.map(group => (
                <SPASectionGroup
                  key={group.slug}
                  editHref={referenceMaterialId ? undefined : sectionEditHref(group.findings[0].section_title)}
                  findings={group.findings}
                  slug={group.slug}
                />
              ))}
            </div>
          )}

          {mustEditGroups.length === 0 && (
            <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-secondary">
              {risks.length > 0
                ? 'No must-edit passages — see the groups below.'
                : 'No passages flagged — nothing to review here.'}
            </p>
          )}

          {editCandidates.length > 0 && (
            <SPADisclosure label={`Edit candidates (${editCandidates.length})`}>
              <div className="tw-flex tw-flex-col tw-gap-5 tw-pl-1 tw-pt-2">
                {editCandidateGroups.map(group => (
                  <SPASectionGroup
                    key={group.slug}
                    editHref={referenceMaterialId ? undefined : sectionEditHref(group.findings[0].section_title)}
                    findings={group.findings}
                    slug={group.slug}
                  />
                ))}
              </div>
            </SPADisclosure>
          )}

          {keepers.length > 0 && (
            <div className="tw-border-0 tw-border-t tw-border-solid tw-border-spa-border-secondary tw-pt-4">
              <SPADisclosure label={`Worth keeping + notes (${keepers.length})`}>
                <div className="tw-pl-1 tw-pt-2">
                  <FindingList showSection findings={keepers} />
                </div>
              </SPADisclosure>
            </div>
          )}
        </>
        )}

        {loading && !report && !isRunning && (
        <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-secondary">Loading review…</p>
        )}

        {!auditable && !report && (
          <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-secondary">
            Nothing to audit — this material is image-only, with no text or questions for a text review to read.
          </p>
        )}

        {auditable && !report && !isRunning && !loading && status !== 'rate_limited' && !error && (
        <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-secondary">
          No review yet. Run one to flag adoption-risk passages in
          {referenceMaterialId ? ' this material’s text and questions.' : ' this lesson’s reading and notebook.'}
        </p>
        )}
      </div>
    </section>
  );
};

export default AuditReportPane;
